import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { API } from '../../../API';
import {
  EmbeddingContext,
  ExecutionPollResult,
  ExecutionStatus,
  isTerminalStatus,
  RunResponse,
  WindmillApp,
} from '../types';

interface UseWindmillExecutionResult {
  /** Dispatches a run and resolves with its execution id (null on failure). */
  run: (input: Record<string, unknown>) => Promise<string | null>;
  resume: (executionId: string) => void;
  /** Folds an execution fetched elsewhere (e.g. a cancel response) into state. */
  applyExecution: (execution: ExecutionPollResult) => void;
  executionId: string | null;
  status: ExecutionStatus | null;
  /** Parameter values this run was started with. */
  input: Record<string, unknown> | null;
  output: unknown | null;
  error: string | null;
  isRunning: boolean;
  durationMs: number | null;
  completedAt: string | null;
  reset: () => void;
}

const POLL_INTERVAL_MS = 30000;

export function useWindmillExecution(
  app: WindmillApp | null,
  /** Set when the app is run from an embedded placement (case page, message card). */
  embedding?: EmbeddingContext,
): UseWindmillExecutionResult {
  const { t } = useTranslation();
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [status, setStatus] = useState<ExecutionStatus | null>(null);
  const [input, setInput] = useState<Record<string, unknown> | null>(null);
  const [output, setOutput] = useState<unknown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setExecutionId(null);
    setStatus(null);
    setInput(null);
    setOutput(null);
    setError(null);
    setIsRunning(false);
    setDurationMs(null);
    setCompletedAt(null);
  }, [stopPolling]);

  // Allowlist: only genuine failures get a message. A terminal status that is
  // not a failure (`canceled`, or anything the backend adds later) falls
  // through to null, so no error banner is shown for it.
  const genericErrorForStatus = useCallback(
    (s: ExecutionStatus): string | null => {
      if (s === 'failed') return t('Execution failed');
      if (s === 'timeout') return t('Execution timed out');
      return null;
    },
    [t],
  );

  /**
   * Folds an execution snapshot into state. Returns true once the run is
   * terminal, at which point polling has been stopped.
   */
  const applyExecution = useCallback(
    (execution: ExecutionPollResult): boolean => {
      setStatus(execution.status);
      // `input` is always sent by the backend; only overwrite what we already
      // know when it actually came back, so an older response cannot blank it.
      if (execution.input) {
        setInput(execution.input);
      }

      if (!isTerminalStatus(execution.status)) return false;

      stopPolling();
      setOutput(execution.output ?? null);
      setError(genericErrorForStatus(execution.status));
      setDurationMs(execution.duration_ms ?? null);
      setCompletedAt(execution.completed_at ?? null);
      setIsRunning(false);
      return true;
    },
    [stopPolling, genericErrorForStatus],
  );

  // Poll recursively so requests never overlap: the next tick is only
  // scheduled once the previous response has been handled.
  const startPolling = useCallback(
    (id: string, delayMs: number) => {
      const pollOnce = async () => {
        try {
          const poll: ExecutionPollResult = await API.fetch(
            'GET',
            `/API/v2/windmill/executions/${id}`,
          );

          // Bail if reset/stopPolling cleared the timer while we were
          // awaiting the response.
          if (pollingRef.current === null) return;

          if (applyExecution(poll)) return;

          pollingRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
        } catch {
          stopPolling();
          setStatus('failed');
          setError(t('Failed to poll execution status'));
          setIsRunning(false);
        }
      };
      pollingRef.current = setTimeout(pollOnce, delayMs);
    },
    [stopPolling, t, applyExecution],
  );

  /**
   * Attach to an execution that was started earlier — a deep link, a page
   * reload or a row picked from the run history — and poll it until it reaches
   * a terminal state. The first request fires immediately.
   */
  const resume = useCallback(
    (id: string) => {
      stopPolling();
      setExecutionId(id);
      setStatus('pending');
      setInput(null);
      setOutput(null);
      setError(null);
      setDurationMs(null);
      setCompletedAt(null);
      setIsRunning(true);
      startPolling(id, 0);
    },
    [stopPolling, startPolling],
  );

  const run = useCallback(
    async (values: Record<string, unknown>): Promise<string | null> => {
      if (!app) return null;

      reset();
      setIsRunning(true);
      setStatus('pending');
      setInput(values);

      try {
        const data: RunResponse = await API.fetch(
          'POST',
          `/API/v2/windmill/apps/${app.id}/run`,
          null,
          { input: values, mode: app.default_execution_mode, embedding },
        );

        setExecutionId(data.execution_id);
        setStatus(data.status);

        if (isTerminalStatus(data.status)) {
          setOutput(data.output ?? null);
          setError(genericErrorForStatus(data.status));
          setDurationMs(data.duration_ms ?? null);
          setIsRunning(false);
          return data.execution_id;
        }

        startPolling(data.execution_id, POLL_INTERVAL_MS);
        return data.execution_id;
      } catch (err: unknown) {
        setStatus('failed');
        setError(err instanceof Error ? err.message : t('Failed to run app'));
        setIsRunning(false);
        return null;
      }
    },
    [app, embedding, reset, startPolling, t, genericErrorForStatus],
  );

  return {
    run,
    resume,
    applyExecution,
    executionId,
    status,
    input,
    output,
    error,
    isRunning,
    durationMs,
    completedAt,
    reset,
  };
}
